import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useSavedPresets } from "@/hooks/useSavedPresets";

export const DEFAULT_PROMPT =
    "Transform into a hand-painted anime character, cel-shaded style with bold ink outlines, soft pastel color palette, large expressive eyes, dreamy cinematic lighting";
export const PASSTHROUGH_PROMPT =
    "Show the original scene unchanged. Keep the person's natural appearance, clothing, and background exactly as captured.";
export const REFERENCE_PROMPT = "Enhance and use the reference image realistically";

function readImageFile(e) {
    const file = e.target.files?.[0];
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return null;
    }
    return file;
}

function useReferenceImage() {
    const [referenceFile, setReferenceFile] = useState(null);
    const [referencePreview, setReferencePreview] = useState(null);
    const previewUrlRef = useRef(null);

    useEffect(
        () => () => {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        },
        []
    );

    const setReferenceImage = (file) => {
        setReferenceFile(file);
        setReferencePreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            const next = file ? URL.createObjectURL(file) : null;
            previewUrlRef.current = next;
            return next;
        });
    };

    return { referenceFile, referencePreview, setReferenceImage };
}

function useStyleSaver(prompt) {
    const [showSaveInput, setShowSaveInput] = useState(false);
    const [presetName, setPresetName] = useState("");
    const presets = useSavedPresets();

    const saveCurrentStyle = async () => {
        if (!prompt.trim()) {
            toast.error("Write a prompt first — that's what gets saved");
            return;
        }
        if (!presetName.trim()) {
            toast.error("Give your style a name");
            return;
        }
        try {
            await presets.saveStyle(presetName.trim(), prompt);
            setPresetName("");
            setShowSaveInput(false);
        } catch (e) {
            toast.error("Failed to save style");
        }
    };

    const deleteSavedStyle = async (id) => {
        try {
            await presets.removeStyle(id);
        } catch (e) {
            toast.error("Failed to delete style");
        }
    };

    return {
        savedPresets: presets.savedPresets,
        showSaveInput,
        setShowSaveInput,
        presetName,
        setPresetName,
        saveCurrentStyle,
        deleteSavedStyle,
    };
}

function useReferenceHandlers({ session, reference, prompt, setPrompt, setActivePreset }) {
    const applyReference = (file) => {
        reference.setReferenceImage(file);
        const text = prompt.trim() ? prompt : REFERENCE_PROMPT;
        if (!prompt.trim()) setPrompt(REFERENCE_PROMPT);
        session.applyPrompt(text, file);
    };

    const handleReferenceChange = (e) => {
        const file = readImageFile(e);
        if (!file) return;
        applyReference(file);
        toast.success("Reference image set");
    };

    const clearReference = () => {
        reference.setReferenceImage(null);
        session.applyPrompt(prompt, null);
    };

    const handleCustomUpload = (e) => {
        const file = readImageFile(e);
        if (!file) return;
        setActivePreset("custom");
        applyReference(file);
        toast.success("Custom style applied");
        e.target.value = "";
    };

    return { handleReferenceChange, clearReference, handleCustomUpload };
}

export function useStudioControls(session) {
    const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
    const [activePreset, setActivePreset] = useState("anime");
    const [orientation, setOrientation] = useState("landscape");
    const reference = useReferenceImage();
    const saver = useStyleSaver(prompt);
    const refHandlers = useReferenceHandlers({ session, reference, prompt, setPrompt, setActivePreset });

    const handlePromptChange = (e) => {
        setPrompt(e.target.value);
        setActivePreset(null);
    };

    const handlePresetClick = (preset) => {
        setPrompt(preset.prompt);
        setActivePreset(preset.id);
        session.applyPrompt(preset.prompt);
    };

    const handleNonePreset = () => {
        setActivePreset("none");
        setPrompt("");
        reference.setReferenceImage(null);
        if (session.isConnected()) {
            session.applyPrompt(PASSTHROUGH_PROMPT, null);
            toast.info("Transformation cleared — showing original");
        }
    };

    const submitPrompt = () => {
        session.applyPrompt(prompt, reference.referenceFile);
        toast.success("Prompt applied");
    };

    const handleSavedPresetClick = (p) => {
        setPrompt(p.prompt);
        setActivePreset(`saved-${p.id}`);
        session.applyPrompt(p.prompt);
    };

    const handleStart = (opts = {}) => {
        const text = prompt.trim() || (reference.referenceFile ? REFERENCE_PROMPT : PASSTHROUGH_PROMPT);
        if (!prompt.trim() && reference.referenceFile) setPrompt(REFERENCE_PROMPT);
        session.startSession({
            prompt: text,
            referenceFile: reference.referenceFile,
            orientation,
            speed: opts.speed || "standard",
        });
    };

    return {
        prompt,
        orientation,
        setOrientation,
        referenceFile: reference.referenceFile,
        referencePreview: reference.referencePreview,
        activePreset,
        handlePromptChange,
        handlePresetClick,
        handleNonePreset,
        submitPrompt,
        handleSavedPresetClick,
        handleStart,
        ...refHandlers,
        ...saver,
    };
}
